/* ------------------------------------------------------------
 * Button — two pill variants inspired by getcracked.io
 *   primary   : cream/white fill, dark text (main CTA)
 *   secondary : dark surface, white text, subtle border
 * ---------------------------------------------------------- */

.btn {
  --btn-padX: 20px;
  --btn-padY: 11px;
  --btn-font: 14px;
  --btn-gap: 10px;

  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--btn-gap);
  padding: var(--btn-padY) var(--btn-padX);
  font-family: inherit;
  font-size: var(--btn-font);
  font-weight: 600;
  letter-spacing: -0.005em;
  line-height: 1;
  white-space: nowrap;
  text-decoration: none;
  border-radius: var(--radius-sm);
  border: 1px solid transparent;
  cursor: pointer;
  user-select: none;
  transition: transform var(--dur-base) var(--ease-out),
    background var(--dur-base) var(--ease-out),
    border-color var(--dur-base) var(--ease-out),
    color var(--dur-base) var(--ease-out),
    filter var(--dur-base) var(--ease-out),
    box-shadow var(--dur-base) var(--ease-out);
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.6;
  transform: none !important;
}

.btn:focus-visible {
  outline: none;
  box-shadow: var(--ring);
}

/* ---------- Variants ---------- */

.variant-primary {
  background: var(--accent);
  color: var(--accent-contrast);
  border-color: color-mix(in srgb, var(--accent) 70%, #000 8%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.4),
    0 1px 2px rgba(0, 0, 0, 0.18);
}

.variant-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  filter: brightness(0.9);
}

.variant-primary:active:not(:disabled) {
  transform: translateY(0);
  filter: brightness(0.98);
}

.variant-secondary {
  background: var(--surface);
  color: var(--text);
  border-color: var(--border-strong);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.04),
    0 1px 2px rgba(0, 0, 0, 0.2);
}

.variant-secondary:hover:not(:disabled) {
  transform: translateY(-1px);
  background: var(--surface-hover);
  border-color: var(--border-glow);
  color: var(--text);
}

.variant-secondary:active:not(:disabled) {
  transform: translateY(0);
  background: var(--surface-strong);
}

/* Adjust the primary look in light theme so it reads correctly */
[data-theme='light'] .variant-primary {
  background: var(--text);
  color: var(--bg);
  border-color: var(--text);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    0 1px 2px rgba(10, 10, 11, 0.18);
}

[data-theme='light'] .variant-secondary {
  background: #ffffff;
  color: var(--text);
  border-color: var(--border-strong);
}

/* ---------- Sizes ---------- */

.size-sm {
  --btn-padX: 14px;
  --btn-padY: 8px;
  --btn-font: 12.5px;
  --btn-gap: 6px;
}

.size-md {
  --btn-padX: 20px;
  --btn-padY: 11px;
  --btn-font: 14px;
  --btn-gap: 8px;
}

.size-lg {
  --btn-padX: 21px;
  --btn-padY: 11px;
  --btn-font: 15px;
  --btn-gap: 10px;
}

/* ---------- Parts ---------- */

.fullWidth {
  width: 100%;
}

.label {
  display: inline-flex;
  align-items: center;
}

.leading,
.chevron {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: transform var(--dur-base) var(--ease-out);
}

.chevron {
  opacity: 0.9;
}

.btn:hover:not(:disabled) .chevron {
  transform: translateX(2px);
}
